import Button from '@mui/material/Button';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getUnitServiceContractsQuery } from 'api/queries/ServiceContractQueries';
import { ArrowRight } from 'assets/volvoIcons';
import { DataGridToolbar } from 'library/components/controls/datagrid/DataGridToolbar';
import {
	TypeSafeColDef,
	VolvoDataGrid,
} from 'library/components/controls/datagrid/VolvoDataGrid';
import { getLocalizedDateFormat } from 'library/formatters/DateFormatter';

import {
	mapServiceContractDtoToServiceContract,
	serviceContractTypeTranslationMapping,
} from './mappers';
import { ServiceContract } from './types';

export type ServiceContractGridProps = {
	unitId: number;
	onView: (contract: ServiceContract) => void;
	connectedContracts?: ServiceContract[];
};

export const ServiceContractGrid = ({
	unitId,
	onView,
	connectedContracts = [],
}: ServiceContractGridProps): React.JSX.Element => {
	const { t } = useTranslation(['serviceContracts', 'commonButton']);
	const { data, isLoading } = useQuery({
		...getUnitServiceContractsQuery(unitId),
	});
	const contracts = useMemo(
		() => [
			...(data ?? []).map(mapServiceContractDtoToServiceContract),
			...connectedContracts,
		],
		[data, connectedContracts]
	);

	const columns = useMemo(
		(): TypeSafeColDef<ServiceContract>[] => [
			{
				field: 'id',
				headerName: t('serviceContracts:id'),
				headerAlign: 'left',
				minWidth: 155,
				flex: 0.9,
			},
			{
				field: 'serviceType',
				headerName: t('serviceContracts:type'),
				headerAlign: 'left',
				minWidth: 170,
				flex: 1,
				valueFormatter: (value) =>
					serviceContractTypeTranslationMapping[value] ?? value,
			},
			{
				field: 'startDate',
				type: 'date',
				headerName: t('serviceContracts:start-date'),
				headerAlign: 'left',
				minWidth: 125,
				flex: 0.8,
				valueFormatter: (value) => getLocalizedDateFormat(value),
			},
			{
				field: 'endDate',
				type: 'date',
				headerName: t('serviceContracts:end-date'),
				headerAlign: 'left',
				minWidth: 125,
				flex: 0.8,
				valueFormatter: (value) => getLocalizedDateFormat(value),
			},
			{
				field: 'status',
				headerName: t('serviceContracts:status'),
				headerAlign: 'left',
				minWidth: 125,
				flex: 0.8,
			},
			{
				field: 'businessModel',
				headerName: t('serviceContracts:model'),
				headerAlign: 'left',
				minWidth: 125,
				flex: 0.8,
			},
			{
				field: 'source',
				headerName: t('serviceContracts:source'),
				headerAlign: 'left',
				minWidth: 125,
				flex: 0.8,
			},
			{
				field: 'responsibleBusinessPartner',
				headerName: t('serviceContracts:responsible-business-partner'),
				headerAlign: 'left',
				minWidth: 300,
				flex: 2.5,
			},
			{
				field: 'actions',
				type: 'actions',
				align: 'right',
				width: 115,
				minWidth: 115,
				maxWidth: 115,
				getActions: ({ row }: GridRowParams<ServiceContract>) => [
					<Button
						variant="text"
						key={`open-${row.id}`}
						endIcon={<ArrowRight />}
						onClick={() => onView(row)}>
						{t('commonButton:open')}
					</Button>,
				],
			},
		],
		[t, onView]
	);

	return (
		<VolvoDataGrid
			isLoading={isLoading}
			rows={contracts ?? []}
			columns={columns}
			showToolbar
			slots={{ toolbar: DataGridToolbar }}
		/>
	);
};
